import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ChatViewer,
  ChatViewerHandle,
  followMessagesAtBottom,
  followEveryMessage,
} from 'chat-viewer';

import { Toast } from '@wix/wix-ui-tpa/cssVars';
import { ToastSkin } from '@wix/wix-ui-tpa';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  MessageType,
  Sender,
} from '@wix/ambassador-innovation-widget-v1-message/types';

import {
  messagesModel,
  addMessage,
  selectMessages,
  selectOneChatComposed,
  addUploadFileMessage,
  AddMessagePayload,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { A11Y_CHAT_DIALOG_ID } from '@/constants';
import { usePanoramaTransaction } from '@/hooks';
import { PanoramaTransactions } from '@/panoramaTransactions';

import { useOnEditorSettingsEvent } from 'AiAssistantWidget/Widget/contexts';
import { settingsEvents } from 'AiAssistantWidget/settingsEvents';

import { Container } from '../Container';
import { Header } from '../Header';
import { ChatMessage } from '../ChatMessage';
import { Loader } from '../Loader';
import { InputOneChat } from '../Input/InputOneChat';

import { st, classes, cssStates } from './Chat.st.css';
import { useHistoryLoader } from './useHistoryLoader';
import { useOneChatIntroSequence } from './useOneChatIntroSequence';

export const OneChat: React.FC = () => {
  const { t } = useTranslation();
  const chatRef = useRef<ChatViewerHandle<messagesModel.Message>>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages.visibleMessages);
  const isTyping = useAppSelector(selectMessages.isTyping);
  const isLoaded = useAppSelector(selectMessages.isLoaded);
  const isLoading = useAppSelector(selectMessages.isLoading);
  const loadingFirstPage = useAppSelector(selectMessages.loadingFirstPage);
  const messagesIdle = useAppSelector(selectMessages.isIdle);
  const shouldAddContactForm = useAppSelector(
    selectOneChatComposed.shouldSendContactFormAfterFirstMessage,
  );
  const loadOlderMessages = useHistoryLoader();
  const { isEditor } = useEnvironment();

  usePanoramaTransaction(
    PanoramaTransactions.MESSAGES_LOAD_VIEWER,
    isLoaded || isEditor,
  );
  const [uploadFileErrorToastText, setUploadFileErrorToastText] = useState<
    string | null
  >(null);

  const isBusy = useMemo(
    () => isTyping || !messagesIdle,
    [isTyping, messagesIdle],
  );

  const { isSequenceFulfilled } = useOneChatIntroSequence();

  const [value, setValue] = React.useState('');

  const onInputChange = useCallback((newValue: string) => {
    setValue(newValue);
    chatRef.current?.scrollToIndex(-1, { align: 'end', smooth: true });
  }, []);

  const onUploadFile = useCallback(
    ({ file, error }) => {
      if (error || !file) {
        setUploadFileErrorToastText(
          t(error?.message ?? 'app.widget.UploadFileError'),
        );
        return;
      }

      dispatch(
        addUploadFileMessage({
          body: {
            mainText: '',
            additionalData: [{ file, type: 'file_uploaded' }],
          },
        }),
      );
    },
    [dispatch, t],
  );

  const onSubmit = useCallback(() => {
    if (value.trim().length) {
      const message: AddMessagePayload[] = [{ body: { mainText: value } }];

      if (shouldAddContactForm) {
        message.push({
          body: {
            mainText: t('app.widget.form.beforeText'),
          },
          sender: Sender.ASSISTANT,
          messageType: MessageType.CONTACT_FORM,
        });
      }
      dispatch(addMessage(message));
      setValue('');
    }
  }, [dispatch, value, shouldAddContactForm, t]);

  useOnEditorSettingsEvent(
    settingsEvents.openAssistantMessages,
    useCallback(() => {
      if (!isEditor) {
        return;
      }
      chatRef.current?.scrollToIndex(1, { align: 'start', smooth: true });
    }, [isEditor]),
  );

  useEffect(() => {
    if (isEditor) {
      chatRef.current?.scrollToIndex(0, { align: 'end', smooth: true });
    }
  }, [isEditor, messages]);

  const renderMessage = useCallback(
    (message: messagesModel.Message) => {
      return (
        <ChatMessage
          key={message.id}
          message={message}
          last={message.id === messages.at(-1)?.id}
        />
      );
    },
    [messages],
  );

  const onNewerMessages = useCallback(
    (
      chat: ChatViewerHandle<messagesModel.Message>,
      received: messagesModel.Message[],
    ) => {
      if (isEditor) {
        return chat.scrollToTop({ align: 'end', smooth: true });
      }

      const lastMessage = received.at(-1);
      const isContactForm =
        lastMessage?.messageType === MessageType.CONTACT_FORM;

      if (isContactForm) {
        return chat.scrollToId(lastMessage.id, { align: 'start' });
      }

      const hasUserMessages = received.some(
        (message) => message.sender === Sender.SITE_VISITOR,
      );

      if (hasUserMessages) {
        return followEveryMessage<messagesModel.Message>()(chat, received);
      }

      return followMessagesAtBottom<messagesModel.Message>()(chat, received);
    },
    [isEditor],
  );
  return (
    <Container ariaBusy={isBusy} dialogId={A11Y_CHAT_DIALOG_ID}>
      <Header dialogId={A11Y_CHAT_DIALOG_ID} />
      <div ref={parentRef} className={st(classes.container)}>
        <Toast
          isShown={!!uploadFileErrorToastText}
          shouldShowCloseButton
          skin={ToastSkin.error}
          className={st(classes.uploadFileToastError)}
          children={uploadFileErrorToastText}
          onClose={() => setUploadFileErrorToastText(null)}
        />
        {loadingFirstPage && <Loader />}
        <div
          className={st(classes.wrapper, cssStates({ isVisible: isLoaded }))}
          role="log"
          aria-label={t('app.widget.ariallabel.chatLog')}
          tabIndex={0}
        >
          <ChatViewer<messagesModel.Message>
            ref={chatRef}
            alignment="top"
            className={classes.chatViewer}
            scrollerClassName={classes.scroller}
            overscan={10}
            historyEndOffset={5}
            onHistoryEndReached={loadOlderMessages}
            prefix={isLoading && !isEditor && <Loader />}
            suffix={isTyping && !isEditor && <Loader />}
            messages={messages}
            renderMessage={renderMessage}
            onNewerMessages={onNewerMessages}
          />
        </div>
      </div>
      <InputOneChat
        value={value}
        onChange={onInputChange}
        onSubmit={onSubmit}
        disabled={!isSequenceFulfilled}
        onUploadFile={onUploadFile}
      />
    </Container>
  );
};
